import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle2, AlertTriangle, Loader2, Store } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const PAY_LABEL = {
  paid: '결제완료', pending: '결제대기', failed: '결제실패', partially_refunded: '부분취소',
  refunded: '취소완료', cancelled: '주문취소', partial: '일부 결제완료', partially_cancelled: '일부 취소',
};

const RESULT_MSG = {
  success: { tone: 'ok', text: '카드 승인이 완료되었습니다.' },
  fail: { tone: 'err', text: '결제가 완료되지 않았습니다. 아래 사유를 확인한 뒤 다시 시도할 수 있습니다.' },
  pending: { tone: 'warn', text: '승인 결과를 확인 중입니다. 잠시 후 새로고침해 주세요.' },
};

// LIGHTPAY-CHECKOUT-RECOVERY-1 · 판매자 주문별 마지막 실패 사유 (서버 last_failure 기준 · 성공 처리 없음)
const PG_CODE_MSG = {
  '1604': '카드사 승인에 필요한 정보가 전달되지 않아 PG가 승인을 거절했습니다.',
};
const failureText = (f) => {
  if (!f) return null;
  const code = f.code ? ` (코드 ${f.code})` : '';
  switch (f.kind) {
    case 'user_closed': return '결제창을 닫아 결제가 진행되지 않았습니다.';
    case 'pg_declined': return `카드 승인이 거절되었습니다${code}. ${PG_CODE_MSG[f.code] || f.msg || ''}`.trim();
    case 'auth_failed': return `카드 인증에 실패했습니다${code}. ${f.msg || ''}`.trim();
    case 'checking': return '이전 결제의 승인 결과를 확인 중입니다. 잠시 후 새로고침해 주세요.';
    case 'pg_no_response': return 'PG 응답이 없어 결제가 자동 취소되었습니다. 다시 시도해 주세요.';
    case 'verify_failed': return '결제 정보 검증에 실패해 승인하지 않았습니다.';
    case 'abandoned': return '이전 결제창이 완료되지 않았습니다.';
    default: return '결제가 완료되지 않았습니다.';
  }
};

const submitForm = (action, fields) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.acceptCharset = 'UTF-8';
  Object.entries(fields).forEach(([k, v]) => {
    const input = document.createElement('input');
    input.type = 'hidden'; input.name = k; input.value = v ?? '';
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const subPaid = (s) => s.payment_status === 'paid' || s.payment_status === 'partially_refunded';

// 라이트페이 샘플 기준: pgAsistant.js · SendPay(form, mode) · 콜백 이름 변경 불가
const openLightpay = (scriptUrl, actionUrl, fields, mode, onClose) => new Promise((resolve, reject) => {
  const go = () => {
    document.querySelectorAll('form[name="payInit"]').forEach((f) => f.remove());
    const form = document.createElement('form');
    form.name = 'payInit'; form.method = 'POST'; form.action = '';
    Object.entries(fields).forEach(([k, v]) => {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = k; input.value = v ?? '';
      form.appendChild(input);
    });
    document.body.appendChild(form);
    window.pay_result_submit = () => { window.payResultSubmit(); };
    window.pay_result_close = () => { form.remove(); onClose(); };
    window.SendPay(form, mode);
    resolve();
  };
  if (window.SendPay) { go(); return; }
  const s = document.createElement('script');
  s.src = scriptUrl; s.onload = go; s.onerror = () => reject(new Error('결제창 스크립트를 불러오지 못했습니다.'));
  document.body.appendChild(s);
});

const SellerPayCard = ({ so, busy, onPay }) => {
  const paid = subPaid(so);
  const closed = ['cancelled', 'refunded'].includes(so.status);
  const checking = ['approving', 'needs_recovery'].includes(so.last_attempt_status) && !paid;
  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4 flex items-center gap-3" data-testid={`pay-seller-${so.seller_order_id}`}>
      <Store size={18} className="text-[#FF3B30] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate" data-testid={`pay-seller-name-${so.seller_order_id}`}>{so.seller_name_snapshot || '판매자'}</p>
        <p className="text-[11px] text-gray-500 truncate">{(so.item_names || []).join(', ')}</p>
        <p className="text-xs mt-1" data-testid={`pay-seller-status-${so.seller_order_id}`}>
          {checking ? '승인 확인 중' : (PAY_LABEL[so.payment_status] || so.payment_status || '결제대기')}
        </p>
        {!paid && !closed && so.stock_short && (
          <p className="text-[11px] mt-1 text-red-600" data-testid={`pay-seller-stock-${so.seller_order_id}`}>재고가 부족해 결제할 수 없습니다: {so.stock_short}</p>
        )}
        {!paid && !closed && !so.stock_short && failureText(so.last_failure) && (
          <p className="text-[11px] mt-1 text-amber-700 dark:text-amber-400" data-testid={`pay-seller-failure-${so.seller_order_id}`}>{failureText(so.last_failure)}</p>
        )}
        {closed && <p className="text-[11px] mt-1 text-gray-500" data-testid={`pay-seller-closed-${so.seller_order_id}`}>취소·환불된 주문이라 결제할 수 없습니다.</p>}
      </div>
      <div className="text-right shrink-0">
        <p className="font-black text-sm" data-testid={`pay-seller-amount-${so.seller_order_id}`}>{won(so.totals?.total)}</p>
        {paid ? (
          <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><CheckCircle2 size={14} /> 완료</span>
        ) : (
          <button type="button" disabled={busy || closed || checking || !!so.stock_short || so.status !== 'payment_pending'} onClick={() => onPay(so)}
            className="mt-1 px-4 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-bold disabled:opacity-40 transition-colors"
            data-testid={`pay-seller-btn-${so.seller_order_id}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : (so.last_attempt_status ? '다시 결제' : '카드결제')}
          </button>
        )}
      </div>
    </div>
  );
};

export default function OrderPay() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [loadError, setLoadError] = useState('');
  const result = RESULT_MSG[params.get('r')];

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/payments/lightpay/order/${orderId}`);
      setData(r.data);
      setLoadError('');
    } catch (e) {
      const msg = e?.response?.data?.detail || '주문을 불러오지 못했습니다.';
      setLoadError(msg);
      toast.error(msg);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const pay = async (so) => {
    if (busyId) return;
    setBusyId(so.seller_order_id);
    try {
      const r = await api.post('/payments/lightpay/start', { order_id: orderId, seller_order_id: so.seller_order_id });
      if (r.data.already_paid) { toast.success('이미 결제된 판매자 주문입니다.'); await load(); return; }
      if (r.data.mock) {
        const m = await api.post('/payments/lightpay/mock/auth-result', { ord_no: r.data.ord_no });
        submitForm(m.data.return_url, m.data.fields);
        return;
      }
      await openLightpay(r.data.script_url, r.data.action_url, r.data.fields, r.data.pg_mode, () => {
        toast('결제를 취소했습니다.'); setBusyId('');
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || '결제를 시작하지 못했습니다.');
      setBusyId('');
      load();
    }
  };

  const subs = data?.seller_orders || [];
  const unpaidTotal = subs.filter((s) => !subPaid(s) && !['cancelled', 'refunded'].includes(s.status))
    .reduce((t, s) => t + (s.totals?.total || 0), 0);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] pb-16" data-testid="order-pay-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 space-y-6">
        <div className="flex items-center gap-2">
          <CreditCard className="text-[#FF3B30]" size={22} />
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>카드결제</h1>
          {data?.transport === 'mock' && (
            <span className="ml-2 text-[10px] font-black tracking-widest px-2 py-0.5 rounded bg-amber-100 text-amber-700" data-testid="pay-mock-badge">MOCK TEST</span>
          )}
        </div>
        {result && (
          <div className={`rounded-xl p-3 text-sm flex items-center gap-2 ${result.tone === 'ok' ? 'bg-emerald-50 text-emerald-700' : result.tone === 'err' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}
            data-testid="pay-result-banner">
            {result.tone === 'ok' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} {result.text}
          </div>
        )}
        <p className="text-xs text-gray-500" data-testid="pay-split-notice">
          판매자마다 카드 승인이 <strong>따로</strong> 진행되며, 카드 명세서에도 판매자별로 나뉘어 표시됩니다. 한 판매자의 결제가 실패해도 이미 완료된 판매자 결제는 유지됩니다.
        </p>
        {loadError && !data ? (
          <div className="rounded-xl p-4 bg-red-50 text-red-700 text-sm space-y-2" data-testid="pay-load-error">
            <p>{loadError}</p>
            <Link to="/orders" className="font-bold underline">내 주문 목록으로</Link>
          </div>
        ) : !data ? (
          <div className="text-sm text-gray-400">불러오는 중…</div>
        ) : (
          <div className="space-y-3">
            {subs.map((so) => (
              <SellerPayCard key={so.seller_order_id} so={so} busy={busyId === so.seller_order_id} onPay={pay} />
            ))}
            <div className="rounded-2xl bg-black/[0.03] dark:bg-white/[0.04] p-4 flex items-center justify-between">
              <span className="text-sm">주문 결제상태 · <strong data-testid="pay-order-status">{PAY_LABEL[data.order.payment_status] || '결제대기'}</strong></span>
              <span className="text-sm">남은 결제 <strong className="text-[#FF3B30]" data-testid="pay-remaining">{won(unpaidTotal)}</strong></span>
            </div>
            <Link to={`/orders/${orderId}`} className="block text-center text-sm font-bold underline" data-testid="pay-go-order">주문 상세 보기</Link>
          </div>
        )}
      </div>
    </div>
  );
}
